// Accessible-name helper for section CTAs (task #154 — home Lighthouse SEO 92 -> 100).
//
// The home marketing sections render short, repeated CTA verbs ("Learn more",
// "Buy", "Pricing") whose VISIBLE text is intentionally terse for the design.
// On their own those strings are non-descriptive: Lighthouse's `link-text` SEO
// audit flags "learn more" (and its family) as generic, and several identical
// "Buy"/"Pricing" links pointing at different products are indistinguishable to
// a screen-reader user tabbing the page.
//
// Lighthouse's link-text audit reads the anchor's rendered text (`node.innerText`)
// — NOT its aria-label — so the descriptive words must be part of the link's text
// while staying invisible. We keep the terse visible verb and append a
// visually-hidden (`sr-only`) continuation, e.g. visible "Learn more" + hidden
// "about uDFP20". The rendered innerText ("Learn more about uDFP20") is what both
// Lighthouse and assistive tech read; the visible design is unchanged.
//
// `sectionAccessibleName` extracts the plain product name from a HighlightedText
// value (Portable Text blocks). `ctaHiddenSuffix` returns the continuation to
// append ONLY for generic CTA verbs when a name is available, else `undefined`
// (so a CTA that already has descriptive text is left untouched — zero change for
// the rest of the site).

// CTA verbs that are non-descriptive on their own, mapped to the connector used
// when the section name is appended after the visible verb. Keys are lower-cased,
// trimmed. "learn more" and its family are what Lighthouse's link-text audit
// actually penalizes; the others are included so repeated "Buy"/"Pricing" links
// become distinguishable to screen-reader users too.
const GENERIC_CTA_CONNECTORS = {
  'learn more': 'about',
  learn: 'about',
  more: 'about',
  'read more': 'about',
  buy: '',
  'buy now': '',
  pricing: 'for',
  shop: '',
  'shop now': '',
}

// Pull the plain text of the leading heading from a HighlightedText value.
// Accepts the HighlightedText object ({ text: <blocks> }), a raw Portable Text
// block array, or a plain string. Returns the first heading block's text (the
// product/section name), falling back to the first block. Trailing subtitle
// blocks are ignored so the name stays short ("uDFP20", not the whole tagline).
export function sectionAccessibleName(highlighted) {
  if (!highlighted) return ''
  if (typeof highlighted === 'string') return highlighted.replace(/\s+/g, ' ').trim()

  const blocks = Array.isArray(highlighted)
    ? highlighted
    : (highlighted.text || highlighted.blocks)

  if (!Array.isArray(blocks) || blocks.length === 0) return ''

  const heading =
    blocks.find((b) => typeof b?.style === 'string' && b.style.startsWith('heading')) ||
    blocks[0]

  const text = (heading?.children || [])
    .map((c) => (c && typeof c.text === 'string' ? c.text : ''))
    .join('')

  // Collapse whitespace and drop trailing sentence punctuation so a heading like
  // "VRS NextGen FFB." yields a clean name ("VRS NextGen FFB").
  return text.replace(/\s+/g, ' ').trim().replace(/[.…]+$/, '').trim()
}

// Derive a short destination NAME for a legacy <Cta>/<CtaServer> (Audit C M3).
// Unlike the section CtaV2 — whose caller computes the name from a HighlightedText
// heading and passes an explicit `hiddenSuffix` — the legacy <Cta> only receives
// a `route` and/or `link`, so the destination name is humanised from whichever is
// present: the route slug's last-segment, else the link URL's last path segment.
// Returns '' when neither yields a usable segment (mailto:, '#', hash-only, etc.),
// so ctaHiddenSuffix() then returns undefined and the CTA is left untouched.
//
//   { route: { slug: { current: 'pro-pedals' } } } -> 'Pro Pedals'
//   { link: 'https://vrs.racing/us/shop?x=1' }      -> 'Shop'
//   { link: '#' } / {}                              -> ''
export function ctaLinkName({ route, link } = {}) {
  const slug = route && route.slug && route.slug.current
  let raw = ''
  if (slug) {
    raw = String(slug)
  } else if (typeof link === 'string' && link) {
    const pathOnly = link.split(/[?#]/)[0]
    raw = pathOnly.split('/').filter(Boolean).pop() || ''
  }
  raw = raw.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()
  if (!raw) return ''
  return raw
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// Build the visually-hidden continuation to append after a generic CTA verb so
// the link's full text names its destination. Returns `undefined` when the CTA
// text is already descriptive, or when there is no name to describe (so callers
// simply render nothing extra in those cases).
//
// Examples (visible verb + returned suffix => rendered innerText):
//   "Learn more" + "about uDFP20"  => "Learn more about uDFP20"
//   "Buy"        + "DFP15"         => "Buy DFP15"
//   "Pricing"    + "for VRS Coaching" => "Pricing for VRS Coaching"
export function ctaHiddenSuffix(ctaTitle, name) {
  const verb = String(ctaTitle || '').trim().toLowerCase()
  if (!(verb in GENERIC_CTA_CONNECTORS)) return undefined

  const target = String(name || '').trim()
  if (!target) return undefined

  const connector = GENERIC_CTA_CONNECTORS[verb]
  return connector ? `${connector} ${target}` : target
}
